import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SxProps, Theme } from "@mui/material";
import ConfigUtils from "../../../utils/Config";
import IEntidadFacturacion from "../../../types/EntidadFacturacion";

// Sentinel interno: MUI Select no admite `null` como value, así que la entidad principal
// (entidadId: null) se representa aquí y se traduce de vuelta a null en onChange. La cadena
// vacía representa "todavía sin seleccionar" (value === undefined).
const PRINCIPAL_SENTINEL = "__principal__";

// Hay más de una entidad de facturación ACTIVA — la selección de entidad pasa a ser obligatoria
// en los formularios de creación en vez de preseleccionarse por defecto. Solo cuentan las
// activas: es lo que decide si el selector se muestra, y no puede exigirse un campo oculto.
export const esFacturacionMultiEntidad = (): boolean =>
  ConfigUtils.getDatosFacturacionAdicional().some((e: IEntidadFacturacion) => e.active);

interface ISelectorEntidadFacturacionProps {
  value: string | null | undefined;
  onChange: (entidadId: string | null) => void;
  disabled?: boolean;
  error?: boolean;
  sx?: SxProps<Theme>;
}

// Selector de entidad para CREAR facturas/bonos: solo entidades activas.
// Oculto si solo hay una — cero fricción en el caso de hoy. Con más de una, no se preselecciona
// ninguna (a petición de usuarios: preseleccionar la principal generaba errores por descuido),
// así que hay que marcarlo como obligatorio y validarlo en el envío del formulario.
const SelectorEntidadFacturacion: React.FC<ISelectorEntidadFacturacionProps> = ({ value, onChange, disabled, error, sx }) => {
  const entidades: { entidadId: string | null; nombre: string }[] = [
    { entidadId: null, nombre: ConfigUtils.getDatosFacturacion()?.nombre || "Principal" },
    ...ConfigUtils.getDatosFacturacionAdicional()
      .filter((e: IEntidadFacturacion) => e.active)
      .map((e: IEntidadFacturacion) => ({ entidadId: e._id, nombre: e.nombre })),
  ];

  if (entidades.length <= 1) return null;

  const selected = value === undefined ? "" : (value === null ? PRINCIPAL_SENTINEL : value);

  return (
    <FormControl fullWidth sx={sx} error={error} required>
      <InputLabel id="entidad-facturacion-select-label">Entidad de facturación</InputLabel>
      <Select
        labelId="entidad-facturacion-select-label"
        label="Entidad de facturación"
        disabled={disabled}
        displayEmpty
        value={selected}
        onChange={e => onChange(e.target.value === PRINCIPAL_SENTINEL ? null : e.target.value)}
      >
        {entidades.map(ent => (
          <MenuItem key={ent.entidadId ?? PRINCIPAL_SENTINEL} value={ent.entidadId ?? PRINCIPAL_SENTINEL}>
            {ent.nombre}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>Debes seleccionar una entidad de facturación</FormHelperText>}
    </FormControl>
  );
};

export default SelectorEntidadFacturacion;
